import { CalendarPlus, ChevronLeft, ChevronRight, ListChecks, Lock } from 'lucide-react';
import { parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs';
import { useMemo, useState } from 'react';
import type { WaitlistItem, WaitlistMatch } from '@barbeiropro/contracts';
import { PageHeader } from '@/components/shared/page-header';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBarbers, useBranches, useTimezone } from '@/lib/org';
import { useSession } from '@/lib/session';
import {
  useBlocks,
  useBranchHours,
  useCalendar,
  useHolidays,
  useWaitlist,
  type AppointmentDetail,
  type AppointmentListItem,
  type ScheduleBlockItem,
} from './api';
import { AppointmentFormModal, type CreatePrefill } from './appointment-form-modal';
import { AppointmentPanel } from './appointment-panel';
import { BlockDetailModal, BlockFormModal } from './block-modals';
import { closedRanges, gridBounds } from './grid';
import { MonthView } from './month-view';
import { TimeGrid, type GridColumn } from './time-grid';
import { addDays, addMonths, dayStartIso, hhmm, labelDay, labelMonth, labelShort, localIso, startOfMonth, startOfWeek, todayYmd, ymdOf, monthWeeks } from './time';
import { useMediaQuery } from './use-media';
import { WaitlistPanel } from './waitlist-panel';

const VIEWS = ['dia', 'semana', 'mes'] as const;
const VIEW_LABEL = { dia: 'Dia', semana: 'Semana', mes: 'Mês' } as const;
const ALL = '__all';

export function AgendaPage() {
  const tz = useTimezone();
  const today = todayYmd(tz);
  const { can, barberId: ownBarberId } = useSession();
  const restricted = !can('agenda.read_all');
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [p, setP] = useQueryStates({
    data: parseAsString,
    visao: parseAsStringLiteral(VIEWS).withDefault('dia'),
    unidade: parseAsString,
    barbeiro: parseAsString,
  });
  const date = p.data ?? today;
  const view = p.visao;

  const branchesQ = useBranches();
  const barbersQ = useBarbers();
  const activeBranches = (branchesQ.data ?? []).filter((b) => b.active);
  const branchId = p.unidade && activeBranches.some((b) => b.id === p.unidade) ? p.unidade : activeBranches[0]?.id;
  const branchBarbers = useMemo(
    () => (barbersQ.data ?? []).filter((b) => b.active && b.branchId === branchId && (!restricted || b.id === ownBarberId)),
    [barbersQ.data, branchId, restricted, ownBarberId],
  );
  const selectedBarber = restricted ? ownBarberId : branchBarbers.some((b) => b.id === p.barbeiro) ? p.barbeiro : null;

  // período visível
  const range = useMemo(() => {
    if (view === 'dia') return { from: date, to: addDays(date, 1) };
    if (view === 'semana') {
      const s = startOfWeek(date);
      return { from: s, to: addDays(s, 7) };
    }
    const weeks = monthWeeks(date);
    return { from: weeks[0]![0]!, to: addDays(weeks.at(-1)![6]!, 1) };
  }, [view, date]);
  const query = { from: dayStartIso(range.from, tz), to: dayStartIso(range.to, tz), branchId, barberId: selectedBarber ?? undefined };

  const cal = useCalendar(query);
  const blocks = useBlocks({ from: query.from, to: query.to, branchId });
  const hours = useBranchHours(branchId);
  const holidays = useHolidays();
  const waitlist = useWaitlist(date, branchId);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [create, setCreate] = useState<CreatePrefill | null>(null);
  const [blockForm, setBlockForm] = useState(false);
  const [blockView, setBlockView] = useState<ScheduleBlockItem | null>(null);
  const [waitOpen, setWaitOpen] = useState(false);

  const events = cal.data ?? [];
  const hourRows = hours.data ?? [];
  const holidayList = holidays.data ?? [];
  const { startMin, endMin } = useMemo(() => gridBounds(hourRows, events, tz), [hourRows, events, tz]);

  const go = (dir: -1 | 1) => {
    if (view === 'dia') setP({ data: addDays(date, dir) });
    else if (view === 'semana') setP({ data: addDays(date, 7 * dir) });
    else setP({ data: addMonths(startOfMonth(date), dir) });
  };

  const title = view === 'dia' ? labelDay(date) : view === 'semana' ? `${labelShort(startOfWeek(date))} a ${labelShort(addDays(startOfWeek(date), 6))}` : labelMonth(date);

  // colunas
  const columns: GridColumn[] = useMemo(() => {
    const blockFor = (d: string, barberId?: string) =>
      (blocks.data ?? []).filter((b) => ymdOf(b.startsAt, tz) <= d && ymdOf(new Date(new Date(b.endsAt).getTime() - 1), tz) >= d && (b.barberId === null || !barberId || b.barberId === barberId));
    if (view === 'dia') {
      const list = selectedBarber
        ? branchBarbers.filter((b) => b.id === selectedBarber)
        : isMobile
          ? branchBarbers.slice(0, 1)
          : branchBarbers;
      return list.map((b) => ({
        key: b.id,
        title: b.displayName,
        color: b.color,
        date,
        barberId: b.id,
        events: events.filter((e) => e.barberId === b.id && ymdOf(e.startsAt, tz) === date),
        blocks: blockFor(date, b.id),
        closed: closedRanges(date, hourRows, holidayList, branchId, startMin, endMin),
      }));
    }
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(startOfWeek(date), i);
      return {
        key: d,
        title: labelShort(d),
        subtitle: d === today ? 'hoje' : undefined,
        date: d,
        barberId: selectedBarber ?? undefined,
        highlight: d === today,
        events: events.filter((e) => ymdOf(e.startsAt, tz) === d),
        blocks: blockFor(d, selectedBarber ?? undefined),
        closed: closedRanges(d, hourRows, holidayList, branchId, startMin, endMin),
      };
    });
  }, [view, date, branchBarbers, selectedBarber, isMobile, events, blocks.data, hourRows, holidayList, branchId, startMin, endMin, tz, today]);

  const canWrite = can('agenda.write');
  const openCreate = (prefill: CreatePrefill = {}) => setCreate({ branchId, barberId: selectedBarber ?? undefined, date, ...prefill });

  const offerFromEntry = (w: WaitlistItem) => {
    setWaitOpen(false);
    setCreate({
      branchId: w.branchId,
      barberId: w.barberId ?? undefined,
      serviceId: w.serviceId,
      date: w.desiredDate,
      customer: { id: w.customerId, name: w.customerName, phone: w.customerPhone, email: null },
      fromWaitlistId: w.id,
    });
  };
  const offerFromMatch = (m: WaitlistMatch, appt: AppointmentDetail) => {
    setSelectedId(null);
    setCreate({
      branchId: appt.branchId,
      barberId: appt.barberId,
      serviceId: m.serviceId,
      date: ymdOf(appt.startsAt, tz),
      startsAt: appt.startsAt,
      customer: { id: m.customerId, name: m.customerName, phone: m.customerPhone, email: null },
      fromWaitlistId: m.id,
    });
  };

  const barberNameOf = (id: string | null) => branchBarbers.find((b) => b.id === id)?.displayName;
  const waitCount = waitlist.data?.length ?? 0;
  const loadingFirst = (cal.isLoading || branchesQ.isLoading || barbersQ.isLoading) && !cal.data;
  const countLabel = events.length === 1 ? '1 agendamento' : `${events.length} agendamentos`;

  return (
    <>
      <PageHeader
        title="Agenda"
        description={cal.data ? `${countLabel} no período` : undefined}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setWaitOpen(true)}>
              <ListChecks /> Lista de espera{waitCount > 0 ? ` (${waitCount})` : ''}
            </Button>
            {can('agenda.block') && (
              <Button variant="outline" size="sm" onClick={() => setBlockForm(true)}>
                <Lock /> Bloquear horário
              </Button>
            )}
            {canWrite && (
              <Button size="sm" onClick={() => openCreate()}>
                <CalendarPlus /> Novo agendamento
              </Button>
            )}
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon-sm" aria-label="Período anterior" onClick={() => go(-1)}>
            <ChevronLeft />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setP({ data: null })} disabled={date === today && view !== 'mes'}>
            Hoje
          </Button>
          <Button variant="outline" size="icon-sm" aria-label="Próximo período" onClick={() => go(1)}>
            <ChevronRight />
          </Button>
        </div>
        <h2 className="min-w-0 flex-1 truncate text-base font-medium first-letter:uppercase sm:flex-none sm:px-2">{title}</h2>
        <input
          type="date"
          aria-label="Ir para a data"
          value={date}
          onChange={(e) => e.target.value && setP({ data: e.target.value })}
          className="h-8 rounded-md border bg-background px-2 text-sm"
        />
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div role="group" aria-label="Visão" className="inline-flex rounded-md border p-0.5">
            {VIEWS.map((v) => (
              <Button key={v} size="xs" variant={view === v ? 'default' : 'ghost'} aria-pressed={view === v} onClick={() => setP({ visao: v })}>
                {VIEW_LABEL[v]}
              </Button>
            ))}
          </div>
          {activeBranches.length > 1 && (
            <Select value={branchId ?? ''} onValueChange={(v) => setP({ unidade: v, barbeiro: null })}>
              <SelectTrigger size="sm" aria-label="Unidade" className="min-w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {activeBranches.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {!restricted && (
            <Select value={selectedBarber ?? (isMobile && view === 'dia' ? (branchBarbers[0]?.id ?? ALL) : ALL)} onValueChange={(v) => setP({ barbeiro: v === ALL ? null : v })}>
              <SelectTrigger size="sm" aria-label="Profissional" className="min-w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {!(isMobile && view === 'dia') && <SelectItem value={ALL}>Todos os profissionais</SelectItem>}
                {branchBarbers.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {loadingFirst && <LoadingState label="Carregando agenda…" />}
      {cal.isError && !cal.data && <ErrorState error={cal.error} onRetry={() => cal.refetch()} />}

      {!loadingFirst && !(cal.isError && !cal.data) && (
        <>
          {!activeBranches.length ? (
            <div className="rounded-lg border border-dashed px-6 py-12 text-center text-sm text-muted-foreground">Cadastre uma unidade em Configurações para começar a usar a agenda.</div>
          ) : view === 'mes' ? (
            <MonthView
              date={date}
              today={today}
              tz={tz}
              events={events}
              onPickDay={(d) => setP({ data: d, visao: 'dia' })}
            />
          ) : view === 'dia' && columns.length === 0 ? (
            <div className="rounded-lg border border-dashed px-6 py-12 text-center text-sm text-muted-foreground">Nenhum profissional ativo nesta unidade.</div>
          ) : (
            <>
              {events.length === 0 && !cal.isFetching && (
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
                  <span>Nenhum agendamento neste período.</span>
                  {canWrite && (
                    <Button size="sm" variant="outline" onClick={() => openCreate()}>
                      Criar agendamento
                    </Button>
                  )}
                </div>
              )}
              <TimeGrid
                columns={columns}
                tz={tz}
                startMin={startMin}
                endMin={endMin}
                today={today}
                colorByBarber={view === 'semana' && !selectedBarber}
                ariaLabel={view === 'dia' ? 'Agenda do dia por profissional' : 'Agenda da semana'}
                onSlotClick={
                  canWrite
                    ? (col, min) =>
                        openCreate({
                          barberId: col.barberId,
                          date: col.date,
                          startsAt: localIso(col.date, hhmm(min), tz),
                        })
                    : undefined
                }
                onEventClick={(e: AppointmentListItem) => setSelectedId(e.id)}
                onBlockClick={setBlockView}
              />
              <p className="mt-2 text-xs text-muted-foreground">Áreas hachuradas estão fechadas ou bloqueadas. Clique em um horário vazio para agendar.</p>
            </>
          )}
        </>
      )}

      <AppointmentPanel id={selectedId} onClose={() => setSelectedId(null)} onOfferWaitlist={offerFromMatch} />
      {create && (
        <AppointmentFormModal
          open
          onOpenChange={(v) => !v && setCreate(null)}
          prefill={create}
          onCreated={(a) => {
            setP({ data: ymdOf(a.startsAt, tz) });
            setSelectedId(a.id);
          }}
        />
      )}
      <BlockFormModal open={blockForm} onOpenChange={setBlockForm} branchId={branchId} barberId={selectedBarber ?? undefined} date={date} />
      <BlockDetailModal block={blockView} onClose={() => setBlockView(null)} canRemove={can('agenda.block')} barberName={barberNameOf(blockView?.barberId ?? null)} />
      <WaitlistPanel open={waitOpen} onOpenChange={setWaitOpen} date={date} branchId={branchId} canWrite={canWrite} onSchedule={offerFromEntry} />
    </>
  );
}
